/*
 * Exempeldata för importfliken (steg 1). Deterministisk så att GUI:t ser
 * likadant ut varje gång. Innehåller medvetet dubbletter och överlapp så att
 * alla statusar syns.
 */
import { createBundle, nextLocalId, stableHash, buildExerciseMappings } from './importModel.js';
import { buildActivityTypeMappings } from './googleTakeout.js';

// Fallback när aktivitetsbiblioteket inte kan hämtas från API:t.
export const FALLBACK_ACTIVITY_TYPES = [
  { id: 1, key: 'walking_city', nameSv: 'Stadspromenad', nameEn: 'Walking, city pace', category: 'WALK', isDistanceBased: true, isCardio: true, googleFitType: 'walking' },
  { id: 2, key: 'hiking_forest', nameSv: 'Skogsvandring', nameEn: 'Hiking, forest', category: 'HIKE', isDistanceBased: true, isCardio: true, googleFitType: 'hiking' },
  { id: 3, key: 'jogging', nameSv: 'Jogging', nameEn: 'Jogging', category: 'RUN', isDistanceBased: true, isCardio: true, googleFitType: 'running.jogging' },
  { id: 4, key: 'running', nameSv: 'Löpning', nameEn: 'Running (~9.7 km/h)', category: 'RUN', isDistanceBased: true, isCardio: true, googleFitType: 'running' },
  { id: 5, key: 'cycling', nameSv: 'Cykling', nameEn: 'Cycling (~19 km/h)', category: 'CYCLE', isDistanceBased: true, isCardio: true, googleFitType: 'biking' },
  { id: 6, key: 'hema', nameSv: 'HEMA/fäktning', nameEn: 'HEMA / historical fencing', category: 'MARTIAL_ARTS', isDistanceBased: false, isCardio: true, googleFitType: 'fencing' },
  { id: 7, key: 'strength_other', nameSv: 'Styrketräning (övrigt)', nameEn: 'Strength training, other', category: 'STRENGTH', isDistanceBased: false, isCardio: false, googleFitType: 'strength_training' },
  { id: 8, key: 'swimming', nameSv: 'Simning', nameEn: 'Swimming, moderate', category: 'SWIM', isDistanceBased: true, isCardio: true, googleFitType: 'swimming' },
  { id: 9, key: 'other', nameSv: 'Annat', nameEn: 'Other activity', category: 'OTHER', isDistanceBased: false, isCardio: false, googleFitType: 'other' },
];

const local = (y, m, d, h = 0, mi = 0) => new Date(y, m - 1, d, h, mi, 0);
const iso = d => d.toISOString();
const flag = (i, sel = true) => ({ id: nextLocalId(), status: 'new', statusReason: '', selected: sel, ...i });

// ── Daily Strength ───────────────────────────────────────────────────────

const DS_WORKOUTS = [
  { name: '1 tunga ben',  exercises: [['Leg Press', [[50, 20], [100, 10], [150, 10], [200, 10], [250, 6]]], ['Trap Bar Deadlift', [[70, 10], [120, 6], [170, 3], [220, 1]]], ['Situps maskin', [[40, 10], [60, 10], [80, 10]]]] },
  { name: '2 överkroppen', exercises: [['Barbell Bench Press', [[50, 6], [70, 6], [90, 4], [100, 2]]], ['Wide Grip Lat Pull Down', [[50, 10], [60, 8], [70, 6]]], ['Alternating Dumbbell Hammer Curls', [[14, 10], [16, 8]]], ['Dumbbell Side Bend', [[20, 12], [24, 12]]]] },
  { name: '3 ben',        exercises: [['Barbell Squat', [[60, 8], [80, 6], [100, 4]]], ['Lying Leg Curl', [[40, 10], [50, 8]]], ['Standing Calf Raise Machine', [[80, 15], [100, 12]]], ['Ketelbel promenad 12kg', [[null, null, 300, 480]]]] },
  { name: 'Strongman',    exercises: [["Farmer's Walk", [[80, null, 20, 25], [100, null, 20, 30]]], ['Log', [[60, 5], [70, 3]]], ['Statisk sköld', [[null, null, null, 60], [null, null, null, 75]]], ['Joke', [[200, null, 15, 20]]]] },
];

export function mockDailyStrengthBundle(ctx = {}) {
  const bundle = createBundle('daily_strength', [{ name: 'daily_strength.csv', size: 142916 }, { name: 'body_weight.csv', size: 3120 }, { name: 'measurements.csv', size: 1870 }], { mock: true });
  const ds = bundle.datasets;

  // 14 pass, roterande program, nov 2021 → jan 2022.
  let day = local(2021, 11, 15, 7, 9);
  for (let i = 0; i < 14; i++) {
    const w = DS_WORKOUTS[i % DS_WORKOUTS.length];
    const startedAt = new Date(day.getTime() + (i % 2 ? 10.8 * 3600e3 : 0));
    const exercises = w.exercises.map(([csvName, sets], idx) => ({
      csvName, order: idx + 1,
      sets: sets.map(([weight, reps, dist, dur], si) => ({ order: si + 1, weight, reps, distanceMeters: dist ?? null, durationSeconds: dur ?? null, notes: null })),
    }));
    const setCount = exercises.reduce((n, e) => n + e.sets.length, 0);
    const key = `${iso(startedAt)}|${w.name}`;
    ds.sessions.items.push(flag({
      externalId: `ds:${stableHash(key)}`, name: w.name, startedAt: iso(startedAt), endedAt: null,
      durationSeconds: Math.min(150, Math.max(15, Math.round(setCount * 2.5))) * 60, notes: null, exercises, setCount,
    }));
    day = new Date(day.getTime() + (i % 3 === 2 ? 3 : 2) * 86400e3);
  }
  ds.sessions.skippedCount = 4;

  // Vikt varje vecka (två samma dag → dubblett inom filen).
  let wkg = 104.2;
  for (let i = 0; i < 10; i++) {
    const d = local(2021, 11, 14 + i * 7, 7, 30);
    ds.weights.items.push(flag({ externalId: `ds:w:${stableHash(iso(d))}`, date: iso(d), weightKg: +wkg.toFixed(1), fatPercent: +(28.4 - i * 0.2).toFixed(1), musclePercent: null, waterPercent: null }));
    wkg -= 0.4;
  }
  const dup = local(2021, 12, 5, 20, 15);
  ds.weights.items.push(flag({ externalId: `ds:w:${stableHash(iso(dup))}`, date: iso(dup), weightKg: 103.1, fatPercent: null, musclePercent: null, waterPercent: null }));

  // Kroppsmått en gång i månaden.
  [[2021, 11, 14], [2021, 12, 12], [2022, 1, 9]].forEach(([y, m, d], i) => {
    const date = local(y, m, d, 7, 35);
    ds.measurements.items.push(flag({
      externalId: `ds:m:${stableHash(iso(date))}`, date: iso(date),
      values: { chestCm: 108 - i * 0.5, waistCm: 104 - i * 1.5, hipsCm: 109 - i * 0.7, bicepsLeftCm: 38.5 + i * 0.3, bicepsRightCm: 39 + i * 0.3, thighLeftCm: 66 - i * 0.4, thighRightCm: 66.5 - i * 0.4 },
    }));
  });

  ds.height.items.push(flag({ date: iso(local(2021, 11, 14, 7, 35)), heightCm: 184 }));

  ds.sessions.mappings = buildExerciseMappings(ds.sessions.items, ctx);
  return bundle;
}

// ── Google Takeout ───────────────────────────────────────────────────────

const G_ACTS = [
  // [år, mån, dag, h, min, typ, minuter, meter, steg, kcal, snittpuls, namn]
  [2021, 11, 15, 7, 5,  'strength_training', 68, null, 410, 380, 112, 'Styrketräning'], // överlappar DS-passet 1 tunga ben (07:09)
  [2021, 11, 16, 12, 10, 'walking', 42, 3350, 4300, 190, 98, 'Lunchpromenad'],
  [2021, 11, 18, 17, 30, 'walking', 55, 4400, 5600, 245, 101, 'Promenad'],
  [2021, 11, 20, 10, 0,  'hiking', 154, 9800, 12400, 720, 118, 'Skogsvandring Tyresta'],
  [2021, 11, 21, 9, 15,  'running.jogging', 31, 4600, 4900, 340, 148, 'Joggingtur'],
  [2021, 11, 23, 18, 0,  'fencing', 95, null, 2100, 610, 132, 'HEMA-träning'],
  [2021, 11, 25, 7, 10,  'strength_training', 60, null, 350, 340, 110, 'Styrketräning'],
  [2021, 11, 27, 11, 30, 'biking', 48, 15200, 0, 420, 128, 'Cykeltur'],
  [2021, 11, 28, 13, 0,  'walking', 38, 3000, 3900, 170, 96, 'Promenad'],
  [2021, 11, 30, 18, 5,  'fencing', 90, null, 1900, 580, 130, 'HEMA-träning'],
  [2021, 12, 2, 12, 5,   'walking', 44, 3500, 4500, 200, 99, 'Lunchpromenad'],
  [2021, 12, 4, 10, 30,  'hiking', 121, 7600, 9700, 560, 116, 'Vandring'],
  [2021, 12, 5, 9, 0,    'running', 28, 4900, 5100, 360, 155, 'Löprunda'],
  [2021, 12, 7, 18, 0,   'fencing', 92, null, 2000, 600, 131, 'HEMA-träning'],
  [2021, 12, 9, 7, 12,   'weightlifting', 62, null, 380, 350, 108, 'Gympass'],
  [2021, 12, 11, 12, 0,  'walking', 40, 3200, 4100, 180, 97, 'Promenad'],
  [2021, 12, 12, 14, 30, 'swimming', 45, 1200, 0, 380, 125, 'Simning'],
  [2021, 12, 14, 18, 0,  'fencing', 88, null, 1850, 570, 129, 'HEMA-träning'],
  [2021, 12, 16, 12, 10, 'walking', 47, 3700, 4800, 210, 100, 'Lunchpromenad'],
  [2021, 12, 18, 10, 0,  'biking', 65, 21000, 0, 560, 130, 'Långcykling'],
  [2021, 12, 19, 9, 30,  'running.jogging', 33, 4800, 5000, 350, 147, 'Joggingtur'],
  [2021, 12, 21, 18, 0,  'fencing', 94, null, 2050, 605, 132, 'HEMA-träning'],
  [2021, 12, 23, 13, 0,  'walking', 36, 2800, 3600, 160, 95, 'Promenad'],
  [2021, 12, 26, 11, 0,  'hiking', 140, 8900, 11300, 650, 117, 'Juldagsvandring'],
  [2021, 12, 28, 12, 15, 'walking', 41, 3300, 4200, 185, 98, 'Promenad'],
  [2021, 12, 30, 17, 0,  'kitesurfing', 75, null, 0, 500, 135, 'Kitesurfing'], // ingen mappning i biblioteket
  [2022, 1, 2, 10, 0,    'running', 30, 5200, 5400, 380, 156, 'Nyårslöpning'],
  [2022, 1, 4, 18, 0,    'fencing', 90, null, 1950, 590, 130, 'HEMA-träning'],
  [2022, 1, 6, 12, 0,    'walking', 45, 3600, 4600, 205, 99, 'Lunchpromenad'],
  [2022, 1, 8, 9, 0,     'hiking', 130, 8200, 10500, 600, 115, 'Vandring'],
  [2022, 1, 9, 7, 8,     'strength_training', 66, null, 400, 370, 111, 'Styrketräning'], // överlappar DS-pass
  [2022, 1, 11, 18, 0,   'fencing', 91, null, 1980, 595, 131, 'HEMA-träning'],
  [2022, 1, 13, 12, 5,   'walking', 43, 3400, 4400, 195, 98, 'Promenad'],
  [2022, 1, 15, 10, 30,  'biking', 52, 16800, 0, 450, 129, 'Cykeltur'],
  [2022, 1, 16, 9, 20,   'running.jogging', 32, 4700, 4950, 345, 146, 'Joggingtur'],
  [2022, 1, 18, 18, 0,   'fencing', 93, null, 2020, 600, 132, 'HEMA-träning'],
  [2022, 1, 20, 12, 10,  'walking', 46, 3650, 4700, 208, 100, 'Lunchpromenad'],
  [2022, 1, 22, 11, 0,   'hiking', 118, 7400, 9400, 545, 116, 'Vandring'],
  [2022, 1, 23, 13, 0,   'walking', 39, 3100, 4000, 175, 97, 'Promenad'],
  [2022, 1, 25, 18, 0,   'fencing', 89, null, 1930, 585, 130, 'HEMA-träning'],
  [2022, 1, 27, 12, 0,   'walking', 44, 3500, 4500, 200, 99, 'Promenad'],
];

export function mockGoogleBundle(ctx = {}) {
  const activityTypes = ctx.activityTypes?.length ? ctx.activityTypes : FALLBACK_ACTIVITY_TYPES;
  const bundle = createBundle('google_takeout', [{ name: 'takeout-20260905T101500Z-001.zip', size: 48_300_000 }], { mock: true });
  const ds = bundle.datasets;

  for (const [y, m, d, h, mi, type, min, meters, steps, kcal, hr, name] of G_ACTS) {
    const start = local(y, m, d, h, mi);
    const id = `gfit:${stableHash(`${type}|${iso(start)}`)}`;
    ds.activities.items.push(flag({
      externalId: id, googleType: type, name, startedAt: iso(start), durationSeconds: min * 60,
      distanceMeters: meters, steps: steps || null, kcal, avgHeartRate: hr,
      hasRoute: meters != null && meters > 2000,
    }));
  }

  // Vikt från Google (hälsa-appen / smartvåg) – två datum krockar med gymappen/servern.
  let kg = 103.9;
  for (let i = 0; i < 8; i++) {
    const d = local(2021, 11, 17 + i * 6, 6, 50);
    ds.weights.items.push(flag({ externalId: `gfit:w:${stableHash(iso(d))}`, date: iso(d), weightKg: +kg.toFixed(1), fatPercent: i % 2 ? null : +(27.9 - i * 0.15).toFixed(1), musclePercent: null, waterPercent: null }));
    kg -= 0.35;
  }
  const clash = local(2021, 12, 5, 6, 48);
  ds.weights.items.push(flag({ externalId: `gfit:w:${stableHash(iso(clash))}`, date: iso(clash), weightKg: 103.3, fatPercent: null, musclePercent: null, waterPercent: null }));

  ds.height.items.push(flag({ date: iso(local(2021, 11, 17, 6, 50)), heightCm: 184 }));
  ds.height.items.push(flag({ date: iso(local(2019, 3, 2, 8, 0)), heightCm: 183.5 }));

  // Dagliga steg, 45 dagar.
  const stepSeed = [6200, 8400, 4100, 11300, 7600, 9800, 3200];
  for (let i = 0; i < 45; i++) {
    const d = local(2021, 11, 15 + i, 12, 0);
    const steps = stepSeed[i % 7] + ((i * 37) % 900);
    ds.steps.items.push(flag({ externalId: `gfit:steps:${d.toDateString()}`, date: iso(d), steps }));
  }

  ds.sleep.skippedCount = 44;
  ds.heartRate.skippedCount = 1180;

  ds.activities.typeMappings = buildActivityTypeMappings(ds.activities.items, activityTypes);
  return bundle;
}

// ── Stor exempeldata: flera års Takeout (~170 MB i verkligheten) ─────────
// Deterministisk pseudoslump så att GUI:t kan lagg-testas mot tusentals poster.
function rng(seed) { let x = seed >>> 0; return () => { x = (x * 1664525 + 1013904223) >>> 0; return x / 4294967296; }; }

const LARGE_TYPES = [
  ['walking', 'Promenad', 30, 60, 4.5, 95],
  ['walking', 'Lunchpromenad', 35, 50, 4.8, 98],
  ['running.jogging', 'Joggingtur', 25, 45, 9.2, 147],
  ['biking', 'Cykeltur', 40, 90, 19, 128],
  ['hiking', 'Vandring', 90, 200, 4.0, 116],
  ['fencing', 'HEMA-träning', 85, 100, null, 131],
  ['strength_training', 'Styrketräning', 55, 75, null, 110],
  ['swimming', 'Simning', 30, 50, 1.6, 125],
  ['kitesurfing', 'Kitesurfing', 60, 90, null, 135],
];

export function mockGoogleBundleLarge(ctx = {}, { years = 6 } = {}) {
  const activityTypes = ctx.activityTypes?.length ? ctx.activityTypes : FALLBACK_ACTIVITY_TYPES;
  const rand = rng(20260905);
  const bundle = createBundle('google_takeout', [{ name: `takeout-${years}ar-001.zip`, size: 172_400_000 }], { mock: true });
  const ds = bundle.datasets;
  const end = local(2026, 8, 31, 12, 0);
  const start = new Date(end.getTime() - years * 365.25 * 86400e3);
  let kg = 112;
  for (let d = new Date(start); d <= end; d = new Date(d.getTime() + 86400e3)) {
    const dow = d.getDay();
    // 0–3 aktiviteter per dag, fler på helger
    const n = rand() < (dow === 0 || dow === 6 ? 0.85 : 0.65) ? (rand() < 0.25 ? 2 : 1) : 0;
    for (let k = 0; k < n; k++) {
      const t = LARGE_TYPES[Math.floor(rand() * LARGE_TYPES.length)];
      const [type, name, minMin, maxMin, kmh, hr] = t;
      const min = Math.round(minMin + rand() * (maxMin - minMin));
      const startAt = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 6 + Math.floor(rand() * 14), Math.floor(rand() * 60));
      const meters = kmh ? Math.round(kmh * 1000 * min / 60 * (0.85 + rand() * 0.3)) : null;
      ds.activities.items.push(flag({
        externalId: `gfit:${stableHash(`${type}|${iso(startAt)}`)}`, googleType: type, name,
        startedAt: iso(startAt), durationSeconds: min * 60, distanceMeters: meters,
        steps: type.startsWith('walking') || type.startsWith('running') || type === 'hiking' ? Math.round(meters / 0.75) : null,
        kcal: Math.round(min * (4 + rand() * 6)), avgHeartRate: Math.round(hr + (rand() - 0.5) * 12),
        hasRoute: !!meters && meters > 2000,
      }));
    }
    // Vikt ungefär var annan dag, långsamt nedåt med brus
    if (rand() < 0.5) {
      kg += (rand() - 0.53) * 0.5;
      const w = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 6, 45);
      ds.weights.items.push(flag({ externalId: `gfit:w:${stableHash(iso(w))}`, date: iso(w), weightKg: +kg.toFixed(1), fatPercent: rand() < 0.4 ? +(22 + (kg - 95) * 0.3).toFixed(1) : null, musclePercent: null, waterPercent: null }));
    }
    // Steg varje dag
    ds.steps.items.push(flag({ externalId: `gfit:steps:${d.toDateString()}`, date: iso(new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12)), steps: Math.round(2500 + rand() * 9000 + n * 2500) }));
  }
  ds.height.items.push(flag({ date: iso(local(2024, 2, 11, 8, 0)), heightCm: 184 }));
  ds.height.items.push(flag({ date: iso(local(2020, 5, 3, 8, 0)), heightCm: 183.5 }));
  ds.sleep.skippedCount = Math.round(years * 340);
  ds.heartRate.skippedCount = Math.round(years * 365 * 260);
  ds.activities.typeMappings = buildActivityTypeMappings(ds.activities.items, activityTypes);
  return bundle;
}

// Stor Daily Strength: ~5 års pass (2–3 per vecka) med ~80 övningsnamn.
const LARGE_EXERCISES = [
  'Barbell Bench Press', 'Dumbbell Bench Press', 'Incline Dumbbell Press', 'Decline Dumbbell Bench Press', 'Butterfly Machine', 'Cable Fly',
  'Leg Press', 'Barbell Squat', 'Barbell Front Squat', 'Squat med bänk', 'Hack Squat', 'Leg Extensions', 'Lying Leg Curl', 'Seated Leg Curl',
  'Standing Calf Raise Machine', 'Seated Calf Raise', 'Walking Lunges', 'Bulgarian Split Squat',
  'Trap Bar Deadlift', 'Barbell Deadlifts', 'Barbell Romanian Deadlift', 'Fat grep marklyft', 'Utlås träning dead lift', 'Sumo Deadlift',
  'Wide Grip Lat Pull Down', 'Cable Underhand Pulldown', 'Seated Cable Rows', 'Barbell Row', 'Dumbbell Row', 'Chin Ups', 'Hyperextensions', 'Face Pull',
  'Standing Barbell Military Press', 'Seated Barbell Military Press', 'Arnold Press', 'Dumbbell Shoulder Press', 'Alternating Dumbbell Front Raises',
  'Dumbbell Lateral Raise', 'Dumbbell Cuban Press', 'Rear Delt Fly', 'Barbell Shrugs',
  'Alternating Dumbbell Hammer Curls', 'Alternating Dumbbell Bicep Curls', 'Dumbbell Bicep Curl', 'Barbell Curl', 'Preacher Curl', 'Cable Triceps Pushdown',
  'Skull Crushers', 'Overhead Triceps Extension', 'Dips', 'Palms-Up Barbell Wrist Curl Over A Bench', 'Reverse Wrist Curl',
  'Situps maskin', 'Dumbbell Side Bend', 'Hanging Leg Raise', 'Plank', 'Cable Crunch', 'Russian Twist', 'Ab Wheel',
  "Farmer's Walk", 'Log', 'Joke', 'Statisk sköld', 'Sircus dubbel (med stuts)', 'Hender', 'Atlas Stone', 'Sandbag Carry', 'Ketelbel promenad 12kg', 'Sled Push',
  'Kettlebell Swing', 'Turkish Get-up', 'Box Jump', 'Battle Ropes', 'Rowing Machine', 'Assault Bike', 'Walking', 'Stairmaster', 'Burpees', 'Wall Balls',
];
const LARGE_PROGRAMS = [
  ['1 tunga ben', [6, 7, 8, 14, 18, 52]], ['2 överkroppen', [0, 1, 24, 41, 53, 32]], ['3 ben', [9, 11, 12, 15, 19, 60]],
  ['4 push', [2, 3, 5, 33, 37, 45]], ['5 pull', [25, 26, 27, 29, 30, 42]], ['Strongman', [59, 60, 61, 62, 66, 67]],
  ['Armar & mage', [43, 44, 46, 47, 54, 55]], ['Kondition', [73, 74, 75, 76, 77, 78]],
];

export function mockDailyStrengthBundleLarge(ctx = {}, { years = 5 } = {}) {
  const rand = rng(424242);
  const bundle = createBundle('daily_strength', [{ name: 'daily_strength.csv', size: 3_900_000 }, { name: 'body_weight.csv', size: 61_000 }, { name: 'measurements.csv', size: 24_000 }], { mock: true });
  const ds = bundle.datasets;
  const end = local(2026, 8, 30, 7, 0);
  const start = new Date(end.getTime() - years * 365.25 * 86400e3);
  let p = 0, kg = 110;
  for (let d = new Date(start); d <= end; d = new Date(d.getTime() + 86400e3)) {
    const dow = d.getDay();
    if ((dow === 1 || dow === 3 || dow === 5) && rand() < 0.8) {
      const [name, idx] = LARGE_PROGRAMS[p++ % LARGE_PROGRAMS.length];
      const startedAt = new Date(d.getFullYear(), d.getMonth(), d.getDate(), rand() < 0.6 ? 7 : 17, Math.floor(rand() * 50));
      const exercises = idx.slice(0, 4 + Math.floor(rand() * 3)).map((ei, order) => {
        const csvName = LARGE_EXERCISES[ei];
        const cardio = ei >= 73;
        const nSets = cardio ? 1 : 3 + Math.floor(rand() * 3);
        const base = 20 + Math.floor(rand() * 120);
        return {
          csvName, order: order + 1,
          sets: Array.from({ length: nSets }, (_, si) => cardio
            ? { order: 1, weight: null, reps: null, distanceMeters: Math.round(500 + rand() * 4000), durationSeconds: Math.round(300 + rand() * 1500), notes: null }
            : { order: si + 1, weight: base + si * 10, reps: Math.max(1, 12 - si * 2 - Math.floor(rand() * 3)), distanceMeters: null, durationSeconds: null, notes: null }),
        };
      });
      const setCount = exercises.reduce((n, e) => n + e.sets.length, 0);
      const key = `${iso(startedAt)}|${name}`;
      ds.sessions.items.push(flag({
        externalId: `ds:${stableHash(key)}`, name, startedAt: iso(startedAt), endedAt: null,
        durationSeconds: Math.min(150, Math.max(15, Math.round(setCount * 2.5))) * 60, notes: null, exercises, setCount,
      }));
    }
    if (dow === 0 && rand() < 0.9) {
      kg += (rand() - 0.55) * 0.6;
      const w = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 7, 30);
      ds.weights.items.push(flag({ externalId: `ds:w:${stableHash(iso(w))}`, date: iso(w), weightKg: +kg.toFixed(1), fatPercent: +(24 + (kg - 95) * 0.3).toFixed(1), musclePercent: null, waterPercent: null }));
      if (d.getDate() <= 7) {
        ds.measurements.items.push(flag({
          externalId: `ds:m:${stableHash(iso(w))}`, date: iso(w),
          values: { chestCm: +(100 + (kg - 95) * 0.5).toFixed(1), waistCm: +(90 + (kg - 95) * 0.9).toFixed(1), hipsCm: +(104 + (kg - 95) * 0.4).toFixed(1), bicepsLeftCm: 38.5, bicepsRightCm: 39, thighLeftCm: 64, thighRightCm: 64.5 },
        }));
      }
    }
  }
  ds.sessions.skippedCount = 37;
  ds.height.items.push(flag({ date: iso(local(2022, 1, 9, 7, 35)), heightCm: 184 }));
  ds.sessions.mappings = buildExerciseMappings(ds.sessions.items, ctx);
  return bundle;
}

// Vad som "redan finns på servern" i exempelläget.
export function mockExisting() {
  return {
    sessions: [
      { externalId: null, name: '2 överkroppen', startTime: iso(local(2021, 11, 17, 17, 57)), endTime: iso(local(2021, 11, 17, 19, 5)) }, // dubblett av DS-pass #2
      { externalId: null, name: 'Bröst & rygg', startTime: iso(local(2021, 11, 25, 7, 0)), endTime: iso(local(2021, 11, 25, 8, 15)) },     // överlappar Google 25/11
      { externalId: null, name: 'Ben', startTime: iso(local(2021, 12, 9, 7, 0)), endTime: iso(local(2021, 12, 9, 8, 20)) },               // överlappar Google 9/12
    ],
    activities: [
      { externalId: `gfit:${stableHash(`walking|${iso(local(2021, 11, 16, 12, 10))}`)}`, startedAt: iso(local(2021, 11, 16, 12, 10)), durationSeconds: 42 * 60 }, // redan importerad
    ],
    weights: [
      { date: iso(local(2021, 11, 28, 7, 30)) },
      { date: iso(local(2021, 12, 5, 7, 30)) },
    ],
    measurements: [{ date: iso(local(2021, 12, 12, 7, 35)) }],
    steps: [
      { date: iso(local(2021, 11, 15, 12, 0)), steps: 6200 },  // lika → dubblett
      { date: iso(local(2021, 11, 16, 12, 0)), steps: 5000 },  // lägre → uppdaterar
      { date: iso(local(2021, 11, 17, 12, 0)), steps: 9000 },  // högre → dubblett
    ],
  };
}
